import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { api } from '../hooks/useAuth';

const loginSchema = z.object({
  email: z.string().email('validation.email'),
  password: z.string().min(1, 'validation.required'),
});

const Login = () => {
  const { t, i18n } = useTranslation();
  const { login, isLoggingIn, loginError } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  // Fetch system settings for name and language
  const { data: settings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: async () => {
      const response = await api.get('/settings/public');
      return response.data;
    },
    retry: false, // Don't retry on login page
  });

  const systemName = settings?.systemName || 'Invoice System';

  // Apply system language if available
  useEffect(() => {
    if (settings?.defaultLocale) {
      // Only apply if user hasn't manually changed language
      const savedLocale = localStorage.getItem('locale');
      if (!savedLocale) {
        i18n.changeLanguage(settings.defaultLocale);
      }
    }
  }, [settings, i18n]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    try {
      setError('');
      await login(data);
      navigate('/dashboard');
    } catch (err) {
      setError(t('errors.networkError'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-2xl font-bold text-gray-900 mb-2">
            {systemName}
          </h1>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.login')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
              {t('auth.register')}
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                {t('auth.email')}
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder={t('auth.email')}
              />
              {errors.email && (
                <p className="error-message">{t(errors.email.message)}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="label">
                {t('auth.password')}
              </label>
              <input
                {...register('password')}
                type="password"
                autoComplete="current-password"
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder={t('auth.password')}
              />
              {errors.password && (
                <p className="error-message">{t(errors.password.message)}</p>
              )}
            </div>
          </div>

          {(error || loginError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">
                {error || loginError?.response?.data?.message || t('errors.networkError')}
              </p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="btn btn-primary btn-lg w-full"
            >
              {isLoggingIn ? t('common.loading') : t('auth.loginButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
